// React Imports
import { useRef, useState } from 'react'

// MUI Imports
import Button from '@mui/material/Button'
import Popper from '@mui/material/Popper'
import Fade from '@mui/material/Fade'
import Paper from '@mui/material/Paper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MenuList from '@mui/material/MenuList'
import MenuItem from '@mui/material/MenuItem'

// Config Imports
import type { Country } from '@configs/countries'
import { COUNTRIES, COUNTRY_CODES } from '@configs/countries'

// Hook Imports
import { useSettings } from '@core/hooks/useSettings'

// Util Imports
import { getCurrentCountry } from '@/utils/country'

const isArabic = () => {
  try {
    return localStorage.getItem('app-locale') === 'ar'
  } catch {
    return false
  }
}

// Switches between the country dashboards (/sa, /kw, ...). A full-page
// navigation, since the country is part of the URL — the session is shared, so
// nobody has to sign in again.
const CountryDropdown = () => {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const { settings } = useSettings()

  const current = getCurrentCountry()
  const ar = isArabic()

  const countryName = (country: Country) => (ar ? country.name_ar : country.name)

  const handleClose = () => setOpen(false)
  const handleToggle = () => setOpen(prev => !prev)

  const handleSelect = (country: Country) => {
    setOpen(false)

    if (country.code === current.code) return

    window.location.href = `/${country.code.toLowerCase()}/`
  }

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={handleToggle}
        color='secondary'
        size='small'
        className='text-textPrimary min-is-0'
        title={countryName(current)}
      >
        <span className='mie-1'>{current.flag}</span>
        {current.code}
      </Button>
      <Popper
        open={open}
        transition
        disablePortal
        placement='bottom-start'
        anchorEl={anchorRef.current}
        className='min-is-[180px] !mbs-3 z-[1]'
      >
        {({ TransitionProps, placement }) => (
          <Fade
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom-start' ? 'left top' : 'right top' }}
          >
            <Paper className={settings.skin === 'bordered' ? 'border shadow-none' : 'shadow-lg'}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList onKeyDown={handleClose}>
                  {COUNTRY_CODES.map(code => (
                    <MenuItem
                      key={code}
                      onClick={() => handleSelect(COUNTRIES[code])}
                      selected={current.code === code}
                      className='gap-2'
                    >
                      <span>{COUNTRIES[code].flag}</span>
                      {countryName(COUNTRIES[code])}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  )
}

export default CountryDropdown
